import clsx from 'clsx';
import React from 'react';
import FormHelperText from '../../atoms/FormHelperText/FormHelperText';
import FormValidationMessage from '../../atoms/FormValidationMessage/FormValidationMessage';
import useFormFieldAria from '../../hooks/useFormFieldAria';

/**
 * CheckboxGroup - Groups several InputCheckbox controls inside a <fieldset>/<legend>
 * with correct grouping semantics and automatic ARIA wiring (helper text, error).
 *
 * Sibling of FormField: FormField wraps a single input via <label htmlFor>, whereas
 * CheckboxGroup wraps multiple checkboxes via <fieldset>/<legend>. The children are
 * "naked" InputCheckbox controls; the group owns the legend, helper text and error.
 */

type CheckboxGroupProps = {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly helperText?: string;
  readonly isDisabled?: boolean;
  /**
   * Visually hides the `<legend>` while keeping it in the DOM as the group's
   * accessible name. Use when the group is already labelled visually by a
   * surrounding element (e.g. an accordion tab title).
   */
  readonly isLegendHidden?: boolean;
  readonly isRequired?: boolean;
  readonly legend: string;
};

function CheckboxGroup({
  children,
  className,
  error = false,
  errorMessage,
  helperText,
  isDisabled = false,
  isLegendHidden = false,
  isRequired = false,
  legend,
}: CheckboxGroupProps): JSX.Element {
  const { ariaDescribedBy, ids } = useFormFieldAria({ error, errorMessage, helperText });

  const shouldShowError = error && !!errorMessage;

  return (
    <fieldset
      aria-describedby={ariaDescribedBy}
      // No `aria-required` here: it is not a valid ARIA attribute on a group/<fieldset>
      // (only on widget roles). `isRequired` is conveyed by the visual asterisk in the legend.
      className={clsx('checkbox-group', className, { 'checkbox-group--error': error })}
      // Native <fieldset disabled> disables every form control inside it, so all child
      // checkboxes become :disabled. A child's own `disabled` still
      // disables that single option when the group as a whole is enabled.
      disabled={isDisabled}
    >
      <legend className={clsx('checkbox-group__legend', { 'checkbox-group__legend--hidden': isLegendHidden })}>
        {legend} {isRequired && <sup aria-hidden="true">*</sup>}
      </legend>

      <div className="checkbox-group__options">{children}</div>

      {helperText && <FormHelperText id={ids.helperId} text={helperText} />}

      {shouldShowError && <FormValidationMessage id={ids.validationId} text={errorMessage} type="error" />}
    </fieldset>
  );
}

export type { CheckboxGroupProps };
export default CheckboxGroup;
